import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { setNeedsOnboarding, setTrialEnd } from '../features/auth/onboardingSlice';

import UnifiedLogin from '../features/auth/UnifiedLogin';
import StaffList from '../features/auth/StaffList';
import Login from '../features/auth/Login.jsx';
import LoginAdmin from '../features/auth/LoginAdmin.jsx';
import Signup from '../features/auth/Signup.jsx';
import ForgotPassword from '../features/auth/ForgotPassword';
import ResetPassword from '../features/auth/ResetPassword';
import PolicyList from '../features/policies/PolicyList';
import MobilePolicyList from '../features/policies/MobilePolicyList';
import ClientList from '../features/clients/ClientList';
import MobileClientList from '../features/clients/MobileClientList';
import CommissionList from '../features/commissions/CommissionList';
import CommissionDetail from '../features/commissions/CommissionDetail';
import MobileCommissionDetail from '../features/commissions/MobileCommissionDetail';
import ClientDetail from '../features/clients/ClientDetail';
import MobileClientDetail from '../features/clients/MobileClientDetail';
import PolicyDetail from '../features/policies/PolicyDetail';
import MobilePolicyDetail from '../features/policies/MobilePolicyDetail';
import BalanceList from '../features/balance/BalanceList';
import MobileBalance from '../features/balance/MobileBalance';
import Renewals from '../features/renewals/Renewals.jsx';
import MobileRenewals from '../features/renewals/MobileRenewals.jsx';
import UploadPolicy from '../features/renewals/UploadPolicy.jsx';
import MeetingsList from '../features/meetings/MeetingsList.jsx';
import SidebarLayout from '../layouts/SidebarLayout';
import MobileLayout from '../layouts/MobileLayout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import OnboardingModal from '../features/auth/OnboardingModal';
import { ScrollRestoration } from '../utils/ScrollRestoration';
import { useStableMobileDetection } from '../utils/deviceDetection';

export default function AppRouter() {
  const dispatch = useDispatch();
  const { user, authChecked } = useSelector((state) => state.auth);
  const { needsOnboarding } = useSelector((state) => state.onboarding);
  const { isMobile, isInitialized } = useStableMobileDetection();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if current user is an admin (phone authentication)
  const [isAdmin, setIsAdmin] = useState(user?.isPhoneUser || false);
  const [adminPhone, setAdminPhone] = useState('');

  useEffect(() => {
    const checkAdminStatus = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const isPhoneUser = currentUser.providerData.some(p => p.providerId === 'phone');
        if (isPhoneUser) {
          let idTokenResult = await currentUser.getIdTokenResult();
          if (idTokenResult.claims.role !== 'admin') {
            idTokenResult = await currentUser.getIdTokenResult(true);
          }
          const finalIsAdmin = idTokenResult.claims.role === 'admin' || isPhoneUser;
          setIsAdmin(finalIsAdmin);
          setAdminPhone(currentUser.phoneNumber || '');

          // Check onboarding status from Firestore
          if (finalIsAdmin) {
            try {
              const adminSnap = await getDoc(doc(db, 'admins', currentUser.uid));
              if (adminSnap.exists()) {
                const adminData = adminSnap.data();
                if (adminData.onboardingComplete === false) {
                  dispatch(setNeedsOnboarding(true));
                } else {
                  dispatch(setNeedsOnboarding(false));
                }
                if (adminData.trialEnd) {
                  dispatch(setTrialEnd(adminData.trialEnd));
                }
              } else if (isPhoneUser) {
                // If phone user but no admin record yet, it's a brand new admin
                // who just authorized. Force onboarding to true.
                console.log('AppRouter: Phone user but no admin record, triggering onboarding');
                dispatch(setNeedsOnboarding(true));
              }
            } catch (err) {
              console.error('Error checking onboarding status:', err);
            }
          }
        } else {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
    const unsubscribe = auth.onAuthStateChanged((user) => {
      checkAdminStatus();
    });
    return () => unsubscribe();
  }, [dispatch]);

  // Clean up Drive OAuth query params from URL after redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has('source') || params.has('drive_linked') || params.has('drive_error')) {
      params.delete('source');
      params.delete('drive_linked');
      params.delete('drive_error');
      const cleanUrl = params.toString()
        ? `${location.pathname}?${params.toString()}`
        : location.pathname;
      navigate(cleanUrl, { replace: true });
    }
  }, [location.search, navigate]);

  if (!authChecked || !isInitialized) {
    return <LoadingSpinner />;
  }

  const isAuthenticated = user || isAdmin;
  const actuallyAdmin = isAdmin || user?.isPhoneUser;

  return (
    <>
      <ScrollRestoration />

      {/* Onboarding Modal - renders on top of authenticated routes for admins */}
      {isAuthenticated && actuallyAdmin && needsOnboarding && (
        <OnboardingModal
          open={true}
          adminPhone={adminPhone}
        />
      )}

      <Routes>
        {!isAuthenticated ? (
          <>
            <Route path="/login" element={<UnifiedLogin />} />
            <Route path="/login-staff" element={<Login />} />
            <Route path="/login-admin" element={<LoginAdmin />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </>
        ) : (
          <>
            {/* Staff routes - accessible by both staff users and admins */}
            <Route element={isMobile ? <MobileLayout /> : <SidebarLayout />}>
              <Route path="/policy" element={isMobile ? <MobilePolicyList /> : <PolicyList />} />
              <Route path="/client" element={isMobile ? <MobileClientList /> : <ClientList />} />
              <Route path="client/:customerId" element={isMobile ? <MobileClientDetail /> : <ClientDetail />} />
              <Route path="/balance" element={isMobile ? <MobileBalance /> : <BalanceList />} />
              <Route path="/renewals" element={isMobile ? <MobileRenewals /> : <Renewals />} />
              <Route path="/upload-policy" element={<UploadPolicy />} />
              <Route path="/staffs" element={<StaffList />} />
              <Route path="/commission" element={<CommissionList />} />
              <Route path="/commission/:id" element={isMobile ? <MobileCommissionDetail /> : <CommissionDetail />} />
              <Route path="/policy/:id" element={isMobile ? <MobilePolicyDetail /> : <PolicyDetail />} />
              <Route path="*" element={<Navigate to={actuallyAdmin ? "/balance" : "/renewals"} />} />
            </Route>
          </>
        )}
      </Routes>
    </>
  );
}